import React, { useState } from 'react'
import { router } from '@inertiajs/react'
import { History, ShieldCheck, ShieldAlert, ChevronRight, FileCode2, Bot, Check, X, AlertTriangle } from 'lucide-react'
import DashboardLayout from '../layouts/DashboardLayout'
import { cn } from '../lib/utils'

const CATEGORY_STYLE = {
  bugfix:  'bg-red-50 text-red-700 border-red-200',
  prompt:  'bg-amber-50 text-amber-700 border-amber-200',
  feature: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  monitor: 'bg-blue-50 text-blue-700 border-blue-200',
  config:  'bg-slate-100 text-slate-700 border-slate-200',
}
const STATUS_STYLE = {
  applied:     'bg-emerald-50 text-emerald-700 border-emerald-200',
  proposed:    'bg-amber-50 text-amber-700 border-amber-200',
  rolled_back: 'bg-slate-100 text-slate-600 border-slate-200',
  escalated:   'bg-red-50 text-red-700 border-red-200',
  approved:    'bg-green-50 text-green-700 border-green-200',
  rejected:    'bg-slate-100 text-slate-500 border-slate-200',
}

function Badge({ children, className }) {
  return <span className={cn('inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium', className)}>{children}</span>
}

function GateBadge({ result }) {
  if (!result) return null
  const green = result === 'green'
  return (
    <Badge className={green ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}>
      {green ? <ShieldCheck className="mr-1 h-3 w-3" /> : <ShieldAlert className="mr-1 h-3 w-3" />}
      gate {result}
    </Badge>
  )
}

function Entry({ log }) {
  const [open, setOpen] = useState(false)
  const files = Array.isArray(log.files) ? log.files : []
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <button onClick={() => setOpen(o => !o)} className="flex w-full items-start justify-between gap-3 text-left">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={CATEGORY_STYLE[log.category] || CATEGORY_STYLE.config}>{log.category}</Badge>
            <Badge className={STATUS_STYLE[log.status] || STATUS_STYLE.applied}>{log.status}</Badge>
            <GateBadge result={log.gate_result} />
            {log.trigger && <span className="text-xs text-gray-400">via {log.trigger}</span>}
          </div>
          <h3 className="mt-1.5 font-semibold text-gray-900">{log.title}</h3>
          {log.summary && <p className="mt-0.5 text-sm text-gray-600 line-clamp-2">{log.summary}</p>}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <span className="whitespace-nowrap text-xs text-gray-400">{log.change_at_label}</span>
          <ChevronRight className={cn('h-4 w-4 text-gray-300 transition-transform', open && 'rotate-90')} />
        </div>
      </button>

      {open && (
        <div className="mt-3 space-y-3 border-t border-gray-100 pt-3 text-sm">
          {log.summary && (<div><div className="text-xs font-semibold uppercase tracking-wide text-gray-400">What changed</div><p className="mt-1 whitespace-pre-wrap text-gray-700">{log.summary}</p></div>)}
          {log.rationale && (<div><div className="text-xs font-semibold uppercase tracking-wide text-gray-400">Why</div><p className="mt-1 whitespace-pre-wrap text-gray-700">{log.rationale}</p></div>)}
          {files.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">Files</div>
              <ul className="mt-1 space-y-0.5">
                {files.map((f, i) => (
                  <li key={i} className="flex items-center gap-1.5 font-mono text-xs text-gray-600"><FileCode2 className="h-3 w-3 text-gray-400" />{f}</li>
                ))}
              </ul>
            </div>
          )}
          {log.reference && <div className="text-xs text-gray-500">Reference: <span className="font-mono">{log.reference}</span></div>}
          {log.detail && Object.keys(log.detail).length > 0 && (
            <pre className="overflow-x-auto rounded bg-gray-50 p-2 text-xs text-gray-600">{JSON.stringify(log.detail, null, 2)}</pre>
          )}
          {log.status === 'proposed' && (
            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={() => router.post(`/ai-change-log/${log.id}/approve`, {}, { preserveScroll: true })}
                className="inline-flex items-center gap-1 rounded-md border border-green-200 bg-green-50 px-3 py-1 text-sm font-medium text-green-700 hover:bg-green-100">
                <Check className="h-4 w-4" /> Approve
              </button>
              <button
                onClick={() => router.post(`/ai-change-log/${log.id}/reject`, { reason: window.prompt('Why reject this proposal? (helps the monitor learn — optional)') || '' }, { preserveScroll: true })}
                className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-3 py-1 text-sm font-medium text-gray-600 hover:bg-gray-50">
                <X className="h-4 w-4" /> Reject
              </button>
              <span className="text-xs text-gray-400">Rejections feed the next self-monitor pass.</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function AiChangeLog({ logs = [], meta = {}, category, categories = [], counts = {} }) {
  const filter = (cat) => router.get('/ai-change-log', cat ? { category: cat } : {}, { preserveState: true, preserveScroll: true })

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-4xl px-4 py-6">
        <div className="mb-5 flex items-start gap-3">
          <div className="rounded-lg bg-indigo-50 p-2"><Bot className="h-6 w-6 text-indigo-600" /></div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">AI Change Log</h1>
            <p className="text-sm text-gray-500">
              Every adjustment the AI assistant made to the system — what changed, why, and whether it passed the regression gate.
              {counts.last_at && <> Last change {counts.last_at}.</>}
              {counts.monitor_last_run && !counts.monitor_stale && <> Self-monitor last ran {counts.monitor_last_run}.</>}
            </p>
          </div>
        </div>

        {counts.monitor_stale && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            The self-monitor hasn't run in over 24h{counts.monitor_last_run ? ` (last ${counts.monitor_last_run})` : ''} — check the PC's IvoryAISelfMonitor task is running.
          </div>
        )}
        {counts.proposed > 0 && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <strong>{counts.proposed}</strong> proposed change{counts.proposed === 1 ? '' : 's'} awaiting your approve/reject below.
          </div>
        )}

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <button onClick={() => filter(null)} className={cn('rounded-full border px-3 py-1 text-sm', !category ? 'border-indigo-300 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50')}>
            All <span className="text-gray-400">{counts.total || 0}</span>
          </button>
          {categories.map(c => (
            <button key={c} onClick={() => filter(c)} className={cn('rounded-full border px-3 py-1 text-sm capitalize', category === c ? 'border-indigo-300 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50')}>{c}</button>
          ))}
          <div className="ml-auto flex items-center gap-1.5 text-xs text-gray-500">
            <ShieldCheck className="h-3.5 w-3.5 text-green-500" /> {counts.green || 0} shipped green
          </div>
        </div>

        {logs.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-200 py-16 text-center text-gray-400">
            <History className="mx-auto mb-2 h-8 w-8" />
            No changes logged yet.
          </div>
        ) : (
          <div className="space-y-3">
            {logs.map(l => <Entry key={l.id} log={l} />)}
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}
